import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  InputAdornment,
  Chip,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Collapse,
  Paper,
} from '@mui/material';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';
import AppLayout from '../components/AppLayout';
import { apiService } from '../services/api';
import { API_ENDPOINTS } from '../constants/api';
import { formatDate, formatCalendarDateIso, utcMs } from '../utils/dateFormat';
import type { ExpenseClaimDto } from '../types';
import { useExpenseVatOptions } from '../hooks/useExpenseVatOptions';
import { useExchangeRates } from '../hooks/useExchangeRates';
import { ExchangeRateBanner } from '../components/ExchangeRateBanner';
import OrganisationExpensesHelpSidebar from '../components/OrganisationExpensesHelpSidebar';
import { useAgentContext } from '../contexts/AgentContext';
import { usePageAgentContext } from '../hooks/usePageAgentContext';
import { getApiErrorMessage } from '../utils/apiError';
import { couldNotLoad } from '../utils/userFacingCopy';

const STATUS_COLOUR: Record<string, 'default' | 'warning' | 'success' | 'error' | 'info'> = {
  Draft: 'default',
  PendingManagerApproval: 'warning',
  Approved: 'success',
  Rejected: 'error',
  Withdrawn: 'info',
  PendingFinanceApproval: 'warning',
};

const ALL_STATUSES_VALUE = '';

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: ALL_STATUSES_VALUE, label: 'All statuses' },
  { value: 'PendingManagerApproval', label: 'Pending manager approval' },
  { value: 'Approved', label: 'Approved' },
  { value: 'Rejected', label: 'Rejected' },
  { value: 'Draft', label: 'Draft' },
  { value: 'Withdrawn', label: 'Withdrawn' },
  { value: 'PendingFinanceApproval', label: 'Pending finance approval' },
];

interface DepartmentOption {
  id: string;
  name: string;
}

export default function OrganisationExpensesPage() {
  const navigate = useNavigate();
  const { showLegacyHelp } = useAgentContext();
  const { getCurrencySymbol, getCurrencyCode } = useExpenseVatOptions();
  const { rates: exchangeRates, convertToGbp } = useExchangeRates();
  const [claims, setClaims] = useState<ExpenseClaimDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [departments, setDepartments] = useState<DepartmentOption[]>([]);
  const [departmentId, setDepartmentId] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>(ALL_STATUSES_VALUE);
  const [fromDate, setFromDate] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 90);
    d.setHours(0, 0, 0, 0);
    return formatCalendarDateIso(d);
  });
  const [toDate, setToDate] = useState<string>(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return formatCalendarDateIso(d);
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<'department' | 'claimDate' | 'requester' | 'payee' | 'totalGbp' | 'status'>('claimDate');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [helpExpanded, setHelpExpanded] = useState(false);
  usePageAgentContext({
    pageId: 'organisation-expenses',
    pageTitle: 'Organisation Expenses',
    visibleSections: ['filters', 'charts', 'claims-table'],
    suggestedPrompts: [
      'How do I filter this expenses report effectively?',
      'What do the charts represent?',
      'What actions are available on this page?',
    ],
  });

  const [chartsOpen, setChartsOpen] = useState(false);

  const loadFilters = useCallback(async () => {
    try {
      const deptRes = await apiService.get<DepartmentOption[]>(API_ENDPOINTS.REFERENCE_DATA.DEPARTMENTS.BASE);
      setDepartments(Array.isArray(deptRes.data) ? deptRes.data : []);
    } catch {
      setDepartments([]);
    }
  }, []);

  const loadClaims = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (departmentId) params.set('departmentId', departmentId);
      if (statusFilter) params.set('status', statusFilter);
      if (fromDate) params.set('fromDate', fromDate);
      if (toDate) params.set('toDate', toDate);
      const url = `${API_ENDPOINTS.EXPENSE_CLAIMS.ORGANISATION}${params.toString() ? `?${params.toString()}` : ''}`;
      const res = await apiService.get<ExpenseClaimDto[]>(url);
      setClaims(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
            setError(getApiErrorMessage(err, couldNotLoad('organisation expense claims')));
      setClaims([]);
    } finally {
      setLoading(false);
    }
  }, [departmentId, statusFilter, fromDate, toDate]);

  useEffect(() => {
    loadFilters();
  }, [loadFilters]);

  useEffect(() => {
    loadClaims();
  }, [loadClaims]);

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getSortIcon = (field: typeof sortField) => {
    if (sortField !== field) return <UnfoldMoreIcon sx={{ fontSize: 16, opacity: 0.5 }} />;
    return sortDirection === 'asc'
      ? <ArrowUpwardIcon sx={{ fontSize: 16 }} />
      : <ArrowDownwardIcon sx={{ fontSize: 16 }} />;
  };

  const filteredClaims = useMemo(() => {
    let result = claims;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (c) =>
          (c.requesterName ?? '').toLowerCase().includes(q) ||
          c.payee.toLowerCase().includes(q)
      );
    }
    return result;
  }, [claims, searchQuery]);

  const toGbp = useCallback(
    (claim: ExpenseClaimDto, amount: number) => convertToGbp(amount, getCurrencyCode(claim.vatRate)),
    [convertToGbp, getCurrencyCode]
  );

  const chartDataCategory = useMemo(() => {
    const byCategory = new Map<string, number>();
    for (const claim of filteredClaims) {
      const currencyCode = getCurrencyCode(claim.vatRate);
      for (const line of claim.lineItems ?? []) {
        const gbp = convertToGbp(line.amount, currencyCode);
        const name = line.expenseCategoryName ?? line.expenseCategoryGlCode ?? 'Uncategorised';
        byCategory.set(name, (byCategory.get(name) ?? 0) + gbp);
      }
    }
    return Array.from(byCategory.entries())
      .map(([name, value]) => ({ name, value: Math.round(value * 100) / 100 }))
      .filter((d) => d.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [filteredClaims, convertToGbp, getCurrencyCode]);

  const chartDataDepartment = useMemo(() => {
    const byDept = new Map<string, number>();
    for (const claim of filteredClaims) {
      const gbp = toGbp(claim, claim.total);
      const dept = claim.requesterDepartmentName ?? '—';
      byDept.set(dept, (byDept.get(dept) ?? 0) + gbp);
    }
    return Array.from(byDept.entries())
      .map(([name, value]) => ({ name, value: Math.round(value * 100) / 100 }))
      .filter((d) => d.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
  }, [filteredClaims, toGbp]);

  const chartDataApprover = useMemo(() => {
    const byApprover = new Map<string, { count: number; totalGbp: number }>();
    for (const claim of filteredClaims) {
      if (claim.status !== 'Approved' || !claim.approvedByName) continue;
      const gbp = toGbp(claim, claim.total);
      const name = claim.approvedByName;
      const cur = byApprover.get(name) ?? { count: 0, totalGbp: 0 };
      byApprover.set(name, { count: cur.count + 1, totalGbp: cur.totalGbp + gbp });
    }
    return Array.from(byApprover.entries())
      .map(([name, { count, totalGbp }]) => ({ name, count, value: Math.round(totalGbp * 100) / 100 }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
  }, [filteredClaims, toGbp]);

  const chartDataOverTime = useMemo(() => {
    const from = fromDate ? new Date(fromDate) : null;
    const to = toDate ? new Date(toDate) : null;
    const rangeDays = from && to ? Math.ceil((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24)) : null;
    const groupByDay = rangeDays !== null && rangeDays <= 45;

    if (groupByDay && from && to) {
      const byDay = new Map<string, number>();
      for (const claim of filteredClaims) {
        const dayKey = formatCalendarDateIso(claim.claimDate);
        const gbp = toGbp(claim, claim.total);
        byDay.set(dayKey, (byDay.get(dayKey) ?? 0) + gbp);
      }
      const out: { period: string; value: number }[] = [];
      const cursor = new Date(from);
      cursor.setHours(0, 0, 0, 0);
      const end = new Date(to);
      end.setHours(0, 0, 0, 0);
      while (cursor <= end) {
        const key = formatCalendarDateIso(cursor);
        out.push({ period: key, value: Math.round((byDay.get(key) ?? 0) * 100) / 100 });
        cursor.setDate(cursor.getDate() + 1);
      }
      return out;
    }

    const byMonth = new Map<string, number>();
    for (const claim of filteredClaims) {
      const key = formatCalendarDateIso(claim.claimDate).slice(0, 7);
      const gbp = toGbp(claim, claim.total);
      byMonth.set(key, (byMonth.get(key) ?? 0) + gbp);
    }
    return Array.from(byMonth.entries())
      .map(([period, value]) => ({ period, value: Math.round(value * 100) / 100 }))
      .sort((a, b) => a.period.localeCompare(b.period));
  }, [filteredClaims, toGbp, fromDate, toDate]);

  const chartDataStatus = useMemo(() => {
    const byStatus = new Map<string, number>();
    for (const claim of filteredClaims) {
      const gbp = toGbp(claim, claim.total);
      const status = claim.status || '—';
      byStatus.set(status, (byStatus.get(status) ?? 0) + gbp);
    }
    return Array.from(byStatus.entries())
      .map(([name, value]) => ({ name, value: Math.round(value * 100) / 100 }))
      .filter((d) => d.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [filteredClaims, toGbp]);

  const chartDataRequester = useMemo(() => {
    const byRequester = new Map<string, number>();
    for (const claim of filteredClaims) {
      const gbp = toGbp(claim, claim.total);
      const name = claim.requesterName ?? 'Unknown';
      byRequester.set(name, (byRequester.get(name) ?? 0) + gbp);
    }
    return Array.from(byRequester.entries())
      .map(([name, value]) => ({ name, value: Math.round(value * 100) / 100 }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
  }, [filteredClaims, toGbp]);

  const CHART_COLORS = ['#667eea', '#764ba2', '#43e97b', '#38f9d7', '#f093fb', '#f5576c', '#4facfe', '#00f2fe', '#fa709a', '#fee140'];

  const sortedClaims = useMemo(() => {
    return [...filteredClaims].sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;
      if (sortField === 'department') {
        aVal = (a.requesterDepartmentName ?? '').toLowerCase();
        bVal = (b.requesterDepartmentName ?? '').toLowerCase();
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      if (sortField === 'claimDate') {
        aVal = utcMs(a.claimDate);
        bVal = utcMs(b.claimDate);
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
      if (sortField === 'requester') {
        aVal = (a.requesterName ?? '').toLowerCase();
        bVal = (b.requesterName ?? '').toLowerCase();
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      if (sortField === 'payee') {
        aVal = a.payee.toLowerCase();
        bVal = b.payee.toLowerCase();
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      if (sortField === 'totalGbp') {
        aVal = convertToGbp(a.total, getCurrencyCode(a.vatRate));
        bVal = convertToGbp(b.total, getCurrencyCode(b.vatRate));
        return sortDirection === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
      }
      aVal = (a.status ?? '').toLowerCase();
      bVal = (b.status ?? '').toLowerCase();
      return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    });
  }, [filteredClaims, sortField, sortDirection, convertToGbp, getCurrencyCode]);

  const paginatedClaims = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedClaims.slice(start, start + rowsPerPage);
  }, [sortedClaims, page, rowsPerPage]);

  const hasActiveFilters =
    searchQuery.trim() !== '' ||
    departmentId !== '' ||
    statusFilter !== '' ||
    fromDate !== '' ||
    toDate !== '';

  const clearSearch = () => {
    setSearchQuery('');
    setPage(0);
  };
  const clearDepartment = () => {
    setDepartmentId('');
    setPage(0);
  };
  const clearStatus = () => {
    setStatusFilter(ALL_STATUSES_VALUE);
    setPage(0);
  };
  const clearFromDate = () => {
    setFromDate('');
    setPage(0);
  };
  const clearToDate = () => {
    setToDate('');
    setPage(0);
  };

  useEffect(() => {
    setPage(0);
  }, [searchQuery, departmentId, statusFilter, fromDate, toDate]);

  return (
    <AppLayout>
      <Box sx={{ maxWidth: '1400px', mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <IconButton onClick={() => navigate(-1)} sx={{ ml: -1 }} title="Back" aria-label="Back">
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h4" component="h1" fontWeight="bold">
              Organisation Expenses
            </Typography>
          </Box>
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Organisation-wide view of expense claims. Use filters to narrow by department, status, or date range. This is read-only; approve or reject from Team Expenses.
          </Typography>
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <ExchangeRateBanner rates={exchangeRates} />

        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <TextField
                size="small"
                placeholder="Search by requester or payee…"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 280, flex: 1 }}
              />
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="org-exp-department-label">Filter by department</InputLabel>
                <Select
                  labelId="org-exp-department-label"
                  value={departmentId}
                  label="Filter by department"
                  onChange={(e) => {
                    setDepartmentId(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  {departments.map((d) => (
                    <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel id="org-exp-status-label" shrink>Filter by status</InputLabel>
                <Select
                  labelId="org-exp-status-label"
                  value={statusFilter}
                  label="Filter by status"
                  displayEmpty
                  renderValue={(v) => (v === '' ? 'All statuses' : STATUS_OPTIONS.find((o) => o.value === v)?.label ?? v)}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(0);
                  }}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value || 'all'} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="From date"
                type="date"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setPage(0);
                }}
                InputLabelProps={{ shrink: true }}
                sx={{
                  width: 160,
                  '& input::-webkit-calendar-picker-indicator': { cursor: 'pointer' },
                }}
              />
              <TextField
                size="small"
                label="To date"
                type="date"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setPage(0);
                }}
                InputLabelProps={{ shrink: true }}
                sx={{
                  width: 160,
                  '& input::-webkit-calendar-picker-indicator': { cursor: 'pointer' },
                }}
              />
            </Box>
            {hasActiveFilters && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                {searchQuery.trim() !== '' && (
                  <Chip label={`Search: "${searchQuery}"`} onDelete={clearSearch} size="small" color="primary" />
                )}
                {departmentId !== '' && (
                  <Chip
                    label={`Department: ${departments.find((d) => d.id === departmentId)?.name ?? departmentId}`}
                    onDelete={clearDepartment}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
                {statusFilter !== '' && (
                  <Chip
                    label={`Status: ${STATUS_OPTIONS.find((o) => o.value === statusFilter)?.label ?? statusFilter}`}
                    onDelete={clearStatus}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
                {fromDate !== '' && (
                  <Chip label={`From: ${fromDate}`} onDelete={clearFromDate} size="small" variant="outlined" />
                )}
                {toDate !== '' && (
                  <Chip label={`To: ${toDate}`} onDelete={clearToDate} size="small" variant="outlined" />
                )}
              </Box>
            )}
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ mb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              cursor: 'pointer',
              userSelect: 'none',
              px: 2,
              py: 1.5,
              '&:hover': { bgcolor: 'action.hover' },
            }}
            onClick={() => setChartsOpen((o) => !o)}
          >
            <Typography variant="subtitle1" fontWeight="600">
              Charts
            </Typography>
            <IconButton size="small" aria-label={chartsOpen ? 'Collapse' : 'Expand'}>
              {chartsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Collapse in={chartsOpen}>
            <CardContent sx={{ pt: 0 }}>
              {!chartsOpen ? null : loading ? (
                <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'flex-start' }}>
                    {chartDataCategory.length > 0 && (
                      <Paper variant="outlined" sx={{ p: 2, minWidth: 320, flex: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Spend by category</Typography>
                        <Box sx={{ width: '100%', height: 280 }}>
                          <ResponsiveContainer width="100%" height={280}>
                            <PieChart>
                              <Pie
                                data={chartDataCategory}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius="70%"
                                label={({ name, value }) => `${name}: £${value.toFixed(0)}`}
                              >
                                {chartDataCategory.map((_, i) => (
                                  <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                                ))}
                              </Pie>
                              <Tooltip formatter={(v: number) => [`£${v.toFixed(2)}`, '']} />
                              <Legend />
                            </PieChart>
                          </ResponsiveContainer>
                        </Box>
                      </Paper>
                    )}
                    {chartDataStatus.length > 0 && (
                      <Paper variant="outlined" sx={{ p: 2, minWidth: 280, flex: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Spend by status</Typography>
                        <Box sx={{ width: '100%', height: 280 }}>
                          <ResponsiveContainer width="100%" height={280}>
                            <PieChart>
                              <Pie
                                data={chartDataStatus}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius="70%"
                                label={({ name, value }) => `${name}: £${value.toFixed(0)}`}
                              >
                                {chartDataStatus.map((_, i) => (
                                  <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                                ))}
                              </Pie>
                              <Tooltip formatter={(v: number) => [`£${v.toFixed(2)}`, '']} />
                              <Legend />
                            </PieChart>
                          </ResponsiveContainer>
                        </Box>
                      </Paper>
                    )}
                  </Box>

                  {chartDataDepartment.length > 0 && (
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Spend by department (top 10)</Typography>
                      <Box sx={{ width: '100%', minHeight: 200, height: Math.max(200, chartDataDepartment.length * 28) }}>
                        <ResponsiveContainer width="100%" height={Math.max(200, chartDataDepartment.length * 28)}>
                          <BarChart data={chartDataDepartment} layout="vertical" margin={{ top: 8, right: 24, left: 120, bottom: 8 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" tickFormatter={(v) => `£${v}`} />
                            <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11 }} />
                            <Tooltip formatter={(v: number) => [`£${v.toFixed(2)}`, '']} />
                            <Bar dataKey="value" fill="#667eea" name="Total" radius={[0, 4, 4, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    </Paper>
                  )}

                  {(chartDataApprover.length > 0) && (
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Approved by manager</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
                        <Box sx={{ width: '100%', minWidth: 300, flex: 1, height: 280 }}>
                          <ResponsiveContainer width="100%" height={280}>
                            <BarChart data={chartDataApprover} margin={{ top: 8, right: 8, left: 8, bottom: 80 }}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="name" angle={-35} textAnchor="end" height={80} tick={{ fontSize: 11 }} />
                              <YAxis tickFormatter={(v) => `£${v}`} />
                              <Tooltip formatter={(v: number) => [`£${v.toFixed(2)}`, 'Approved']} />
                              <Bar dataKey="value" fill="#764ba2" name="Approved" radius={[4, 4, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </Box>
                        <TableContainer component={Box} sx={{ minWidth: 280, maxWidth: 400 }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell><strong>Manager</strong></TableCell>
                                <TableCell align="right"><strong>#</strong></TableCell>
                                <TableCell align="right"><strong>£ Approved</strong></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {chartDataApprover.map((row) => (
                                <TableRow key={row.name}>
                                  <TableCell>{row.name}</TableCell>
                                  <TableCell align="right">{row.count}</TableCell>
                                  <TableCell align="right">£{row.value.toFixed(2)}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    </Paper>
                  )}

                  {chartDataOverTime.length > 0 && (
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Spend over time (by claim date)</Typography>
                      <Box sx={{ width: '100%', height: 260 }}>
                        <ResponsiveContainer width="100%" height={260}>
                          <LineChart data={chartDataOverTime} margin={{ top: 8, right: 8, left: 8, bottom: 24 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                              dataKey="period"
                              tick={{ fontSize: 11 }}
                              tickFormatter={(p) => (p.length === 10 ? formatDate(p, { format: 'short' }) : `${p.slice(5, 7)}/${p.slice(0, 4)}`)}
                            />
                            <YAxis tickFormatter={(v) => `£${v}`} />
                            <Tooltip
                              formatter={(v: number) => [`£${v.toFixed(2)}`, '']}
                              labelFormatter={(p) => (p.length === 10 ? formatDate(p, { format: 'medium' }) : `Month: ${p}`)}
                            />
                            <Line type="monotone" dataKey="value" stroke="#43e97b" strokeWidth={2} name="Total" dot={{ r: 4 }} />
                          </LineChart>
                        </ResponsiveContainer>
                      </Box>
                    </Paper>
                  )}

                  {chartDataRequester.length > 0 && (
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Top requesters by spend (top 10)</Typography>
                      <Box sx={{ width: '100%', minHeight: 220, height: Math.max(220, chartDataRequester.length * 32) }}>
                        <ResponsiveContainer width="100%" height={Math.max(220, chartDataRequester.length * 32)}>
                          <BarChart data={chartDataRequester} layout="vertical" margin={{ top: 8, right: 24, left: 100, bottom: 8 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" tickFormatter={(v) => `£${v}`} />
                            <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 11 }} />
                            <Tooltip formatter={(v: number) => [`£${v.toFixed(2)}`, '']} />
                            <Bar dataKey="value" fill="#4facfe" name="Total" radius={[0, 4, 4, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    </Paper>
                  )}

                  {!loading && chartDataCategory.length === 0 && chartDataStatus.length === 0 && chartDataDepartment.length === 0 && chartDataApprover.length === 0 && chartDataOverTime.length === 0 && chartDataRequester.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No data to display in charts. Adjust filters or add expense claims.</Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </Collapse>
        </Card>

        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.7 } }} onClick={() => handleSort('department')}>
                            <strong>Department</strong>
                            {getSortIcon('department')}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.7 } }} onClick={() => handleSort('claimDate')}>
                            <strong>Claim date</strong>
                            {getSortIcon('claimDate')}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.7 } }} onClick={() => handleSort('requester')}>
                            <strong>Requester</strong>
                            {getSortIcon('requester')}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.7 } }} onClick={() => handleSort('payee')}>
                            <strong>Payee</strong>
                            {getSortIcon('payee')}
                          </Box>
                        </TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', justifyContent: 'flex-end', '&:hover': { opacity: 0.7 } }} onClick={() => handleSort('totalGbp')}>
                            <strong>Total</strong>
                            {getSortIcon('totalGbp')}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.7 } }} onClick={() => handleSort('status')}>
                            <strong>Status</strong>
                            {getSortIcon('status')}
                          </Box>
                        </TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedClaims.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                            <Typography color="text.secondary">
                              {(departmentId || statusFilter || fromDate || toDate)
                                ? 'No claims match the current filters.'
                                : 'No expense claims in the organisation yet.'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        paginatedClaims.map((claim) => (
                          <TableRow key={claim.id} hover>
                            <TableCell>{claim.requesterDepartmentName ?? '—'}</TableCell>
                            <TableCell>{formatDate(claim.claimDate)}</TableCell>
                            <TableCell>{claim.requesterName ?? 'Unknown'}</TableCell>
                            <TableCell>{claim.payee}</TableCell>
                            <TableCell align="right">{getCurrencySymbol(claim.vatRate)}{claim.total.toFixed(2)}</TableCell>
                            <TableCell align="right">£{convertToGbp(claim.total, getCurrencyCode(claim.vatRate)).toFixed(2)}</TableCell>
                            <TableCell>
                              <Chip size="small" label={claim.status ?? '—'} color={STATUS_COLOUR[claim.status ?? ''] ?? 'default'} />
                            </TableCell>
                            <TableCell align="right">
                              <Button
                                size="small"
                                startIcon={<OpenInNewIcon />}
                                onClick={() => navigate(`/expenses/me/new?edit=${claim.id}`)}
                                sx={{ textTransform: 'none' }}
                              >
                                View
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={sortedClaims.length}
                  page={page}
                  onPageChange={(_, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  sx={{ borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}
                />
              </>
            )}
          </CardContent>
        </Card>
      </Box>
      {showLegacyHelp && (
        <OrganisationExpensesHelpSidebar isExpanded={helpExpanded} onToggle={() => setHelpExpanded(!helpExpanded)} />
      )}
    </AppLayout>
  );
}
